import { useEffect, useMemo, type CSSProperties } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L, { type LatLngBoundsExpression, type LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Accommodation, RouteStop } from '../types';

const stopIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
      <path fill="#2E5030" stroke="#182217" stroke-width="1.25" d="M16 1C7.7 1 1 7.7 1 16c0 10.5 13 23.5 14.2 24.7a1 1 0 0 0 1.6 0C18 39.5 31 26.5 31 16C31 7.7 24.3 1 16 1z"/>
      <circle cx="16" cy="16" r="6.4" fill="#F3E7C9"/>
      <path fill="#7B5A33" d="M12 18c0-3 2-6 4.5-6S21 15 21 18c0 2.1-1.2 4-4.5 4S12 20.1 12 18z"/>
    </svg>
  `)}`,
  iconSize: [32, 42],
  iconAnchor: [16, 40],
  popupAnchor: [0, -36],
});

const stayIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
      <path fill="#182217" stroke="#182217" stroke-width="1.25" d="M16 1C7.7 1 1 7.7 1 16c0 10.5 13 23.5 14.2 24.7a1 1 0 0 0 1.6 0C18 39.5 31 26.5 31 16C31 7.7 24.3 1 16 1z"/>
      <path fill="#FDFCFB" d="M10 18v-6h12v6h-2v-2H12v2zm0 2h12v2H10z"/>
    </svg>
  `)}`,
  iconSize: [32, 42],
  iconAnchor: [16, 40],
  popupAnchor: [0, -36],
});

export interface MapPoint {
  id: string;
  name: string;
  city: string;
  state: string;
  lat: number;
  lng: number;
  kind: 'route-stop' | 'accommodation';
  subtitle?: string;
}

export interface MapViewProps {
  stops?: RouteStop[];
  accommodations?: Accommodation[];
  points?: MapPoint[];
  center?: LatLngExpression;
  zoom?: number;
  fitBounds?: boolean;
  className?: string;
  style?: CSSProperties;
  interactive?: boolean;
}

function FitBounds({ points }: { points: MapPoint[] }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds: LatLngBoundsExpression = points.map((point) => [point.lat, point.lng]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
  }, [map, points]);
  return null;
}

export default function MapView({
  stops = [],
  accommodations = [],
  points,
  center = [44.05, -122.95],
  zoom = 6,
  fitBounds = false,
  className,
  style,
  interactive = true,
}: MapViewProps) {
  const resolvedPoints = useMemo<MapPoint[]>(() => {
    if (points) return points;
    return [
      ...stops.map((stop) => ({
        id: stop.id,
        name: stop.name,
        city: stop.city,
        state: stop.state,
        lat: stop.lat,
        lng: stop.lng,
        kind: 'route-stop' as const,
        subtitle: stop.experiences.join(' / '),
      })),
      ...accommodations.map((stay) => ({
        id: stay.id,
        name: stay.name,
        city: stay.city,
        state: stay.state,
        lat: stay.lat,
        lng: stay.lng,
        kind: 'accommodation' as const,
        subtitle: `${stay.style} / ${stay.priceRange}`,
      })),
    ];
  }, [stops, accommodations, points]);

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      scrollWheelZoom={interactive}
      dragging={interactive}
      doubleClickZoom={interactive}
      zoomControl={interactive}
      className={className}
      style={{ width: '100%', height: '100%', borderRadius: 'inherit', ...style }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      {fitBounds ? <FitBounds points={resolvedPoints} /> : null}
      {resolvedPoints.map((point) => (
        <Marker
          key={`${point.kind}-${point.id}`}
          position={[point.lat, point.lng]}
          icon={point.kind === 'route-stop' ? stopIcon : stayIcon}
        >
          <Popup>
            <div style={{ minWidth: 220 }}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                {point.name}
              </div>
              <div style={{ fontSize: 11, color: '#5C5C5C' }}>
                {point.city}, {point.state}
              </div>
              {point.subtitle ? (
                <div style={{ fontSize: 11, color: '#5C5C5C', marginTop: 6 }}>{point.subtitle}</div>
              ) : null}
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
